import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Car, Loader2, ShieldCheck, Tag, Wallet as WalletIcon } from "lucide-react";
import StripePayment from "../StripePayment";
import DriverConsent from "../DriverConsent";
import { DateField, TimeField } from "../DateTimeFields";
import { api } from "../../lib/api";
import { fmtCents } from "../../lib/format";

const inputCls = "w-full rounded-xl border border-[#27272A] bg-[#0A0D10] px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[#E50914] transition-colors";

// Commercial driver booking flow (V2 P2): category → date/time → rate plan →
// server-authoritative quote → vehicle → promo → checkout hold → Stripe
// authorization (shared StripePayment) or wallet. The server computes every
// cent; this component only displays the quote verbatim.
export default function PropertyBooking({ property, selectedCategory }) {
  const navigate = useNavigate();
  const cats = property.categories || [];
  const [catCode, setCatCode] = useState(selectedCategory || cats[0]?.code || "");
  const cat = cats.find((c) => c.code === catCode) || null;
  const plans = cat?.rate_plans || [];
  const [planId, setPlanId] = useState("");
  const selectedPlan = plans.find((p) => p.rate_plan_id === planId) || null;
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [promo, setPromo] = useState("");
  const [promoOn, setPromoOn] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [vehicles, setVehicles] = useState(null);
  const [vehicleId, setVehicleId] = useState("");
  const [hostFields, setHostFields] = useState([]);
  const [answers, setAnswers] = useState({});
  const [wallet, setWallet] = useState(null);
  const [booking, setBooking] = useState(null);
  const [payment, setPayment] = useState(null);
  const [autoRenew, setAutoRenew] = useState(false);
  const [subPayment, setSubPayment] = useState(null);
  const [busy, setBusy] = useState(false);
  const [idGate, setIdGate] = useState(false);
  // Driver consent (server-authoritative, versioned) + per-booking rules ack.
  const [consent, setConsent] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [rulesAck, setRulesAck] = useState(false);

  useEffect(() => {
    api.myVehicles().then((vs) => { setVehicles(vs); if (vs[0]) setVehicleId(vs[0].vehicle_id); }).catch(() => setVehicles([]));
    api.wallet().then(setWallet).catch(() => {});
    api.driverConsentStatus().then(setConsent).catch(() => {});
  }, []);

  // Host custom questions for this property/category — driver-safe public contract.
  useEffect(() => {
    setAnswers({});
    if (!cat) { setHostFields([]); return; }
    api.publicPropertyFields(property.property_code, cat.code)
      .then((r) => setHostFields(r.fields || []))
      .catch(() => setHostFields([]));
  }, [cat, property.property_code]);

  const startIso = date && time ? `${date}T${time}:00` : null; // naive — server reads property timezone

  const loadQuote = useCallback(async () => {
    if (!cat || !planId || !startIso) { setQuote(null); return; }
    setQuoteBusy(true);
    try {
      const r = await api.commercialQuote({
        property_code: property.property_code, category_code: cat.code,
        rate_plan_id: planId, start: startIso,
        ...(promoOn && promo.trim() ? { promotion_code: promo.trim() } : {}),
      });
      setQuote(r);
    } catch (e) {
      setQuote(null);
      toast.error(e.message);
    } finally {
      setQuoteBusy(false);
    }
  }, [cat, planId, startIso, promoOn, promo, property.property_code]);

  useEffect(() => { loadQuote(); }, [loadQuote]);

  const subscribe = async () => {
    setBusy(true);
    try {
      const s = await api.commercialSubscribe({
        property_code: property.property_code, category_code: cat.code,
        rate_plan_id: planId, auto_renew_consent: true,
      });
      setSubPayment(s);
    } catch (e) {
      if (e.status === 403 && e.message === "IDENTITY_VERIFICATION_REQUIRED") { setIdGate(true); return; }
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const book = async (consentJustAccepted = false) => {
    if (selectedPlan?.kind === "monthly" && autoRenew) { return subscribe(); }
    if (!vehicleId) { toast.error("Select a vehicle for this booking — add one under My Vehicles."); return; }
    const missingField = hostFields.find((f) => f.required && !(answers[f.field_id] ?? "").toString().trim());
    if (missingField) { toast.error(`'${missingField.name}' is required by this property.`); return; }
    // Account-level Driver declaration — required once per declaration version.
    if (!consentJustAccepted && consent && !consent.accepted) { setConsentOpen(true); return; }
    setBusy(true);
    try {
      const b = await api.commercialBookingCreate({
        property_code: property.property_code, category_code: cat.code,
        rate_plan_id: planId, start_time: startIso, vehicle_id: vehicleId,
        booking_rules_acknowledged: true,
        ...(Object.keys(answers).length ? { field_answers: answers } : {}),
        ...(quote?.promo?.applied ? { promotion_code: promo.trim() } : {}),
      });
      setBooking(b);
      const p = await api.checkoutWithOtp(b.booking_id);
      if (p.already) { navigate(`/bookings/${b.booking_id}`); return; }
      setPayment(p);
    } catch (e) {
      if (e.code === "DRIVER_CONSENT_REQUIRED") { setConsentOpen(true); return; }
      if (e.status === 403 && e.message === "IDENTITY_VERIFICATION_REQUIRED") {
        setIdGate(true);
        return;
      }
      toast.error(e.message);
      setBooking(null);
      loadQuote(); // server rejected — re-sync availability/price
    } finally {
      setBusy(false);
    }
  };

  const payWithWallet = async () => {
    if (!booking) return;
    setBusy(true);
    try {
      await api.walletPayBooking(booking.booking_id);
      toast.success("Paid from wallet — your arrival code is ready");
      navigate(`/bookings/${booking.booking_id}`);
    } catch (e) {
      if (e.status === 403 && e.message === "IDENTITY_VERIFICATION_REQUIRED") { setIdGate(true); return; }
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (idGate) {
    return (
      <div className="card-dark p-7 text-center" data-testid="booking-id-gate">
        <ShieldCheck className="w-9 h-9 mx-auto text-[#FF3B45]" />
        <h3 className="type-h3 mt-4">Verify your identity to book</h3>
        <p className="text-sm text-zinc-400 mt-2">A one-time government ID check protects drivers and properties.</p>
        <Link to="/account" className="btn-primary inline-flex mt-5 text-sm">Go to verification</Link>
      </div>
    );
  }

  if (subPayment && selectedPlan) {
    return (
      <div className="card-dark p-6 sm:p-7" data-testid="commercial-subscribe-checkout">
        <p className="section-label">Auto-renew subscription</p>
        <h3 className="type-h3 mt-2">{property.name} — {cat?.name}</h3>
        <p className="text-xs text-zinc-500 mt-1">
          Monthly plan · {fmtCents(selectedPlan.amount_cents, selectedPlan.currency)}/month · renews automatically until you cancel.
          Cancelling stops future renewals — the current paid period stays active.
        </p>
        <div className="mt-6">
          <StripePayment clientSecret={subPayment.client_secret} publishableKey={subPayment.publishable_key}
                         amountLabel={`${fmtCents(selectedPlan.amount_cents, selectedPlan.currency)}/mo`}
                         onSuccess={() => { toast.success("Auto-renew subscription active"); navigate("/bookings"); }}
                         onError={(m) => toast.error(m)} />
        </div>
      </div>
    );
  }

  // Checkout step: hold created → authorize with Stripe (or wallet)
  if (booking && payment && quote) {
    const q = quote.quote;
    return (
      <div className="card-dark p-6 sm:p-7" data-testid="commercial-checkout">
        <p className="section-label">Checkout</p>
        <h3 className="type-h3 mt-2">{property.name} — {quote.category_name}</h3>
        <p className="text-xs text-zinc-500 mt-1">{quote.rate_plan.kind} · held for 10 minutes while you authorize</p>
        <div className="mt-5 space-y-2 text-sm" data-testid="checkout-breakdown">
          <div className="flex justify-between"><span className="text-zinc-400">Base fare</span><span>{fmtCents(q.base_fare_cents, q.currency)}</span></div>
          {q.discount_cents > 0 && <div className="flex justify-between text-emerald-400"><span>Discount</span><span>-{fmtCents(q.discount_cents, q.currency)}</span></div>}
          <div className="flex justify-between"><span className="text-zinc-400">Service fee</span><span>{fmtCents(q.service_fee_cents, q.currency)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Processing fee</span><span>{fmtCents(q.processing_fee_cents, q.currency)}</span></div>
          <div className="flex justify-between"><span className="text-zinc-400">Tax</span><span data-testid="quote-tax">{q.tax_state === "calculated" ? fmtCents(q.tax_cents, q.currency) : "Calculated at confirmation"}</span></div>
          <div className="flex justify-between pt-2 border-t border-white/10 font-bold"><span>Total</span><span data-testid="checkout-total">{fmtCents(q.total_cents, q.currency)}</span></div>
        </div>
        <div className="mt-6">
          <StripePayment clientSecret={payment.client_secret} publishableKey={payment.publishable_key}
                         amountLabel={fmtCents(q.total_cents, q.currency)}
                         onSuccess={() => { toast.success("Payment authorized — your arrival code is ready"); navigate(`/bookings/${booking.booking_id}`); }}
                         onError={(m) => toast.error(m)} />
        </div>
        {wallet && wallet.balance_cents >= q.total_cents && (
          <button onClick={payWithWallet} disabled={busy} data-testid="checkout-wallet-pay"
                  className="btn-secondary w-full mt-3 text-sm disabled:opacity-50">
            <WalletIcon className="w-4 h-4" /> Pay {fmtCents(q.total_cents, q.currency)} from wallet (no processing fee)
          </button>
        )}
      </div>
    );
  }

  const q = quote?.quote;
  return (
    <div className="card-dark p-6 sm:p-7" data-testid="property-booking">
      <p className="section-label">Book this property</p>
      <div className="mt-4 space-y-4">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">Spot type</label>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Spot type">
            {cats.map((c) => (
              <button key={c.code} type="button" role="radio" aria-checked={catCode === c.code}
                      onClick={() => { setCatCode(c.code); setPlanId(""); setQuote(null); }}
                      data-testid={`booking-cat-${c.code}`}
                      className={`px-3.5 py-2 rounded-xl text-xs font-semibold border transition-colors ${catCode === c.code ? "border-[#E50914] bg-[#E50914]/15 text-white" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <DateField label="Date" value={date} onChange={(v) => { setDate(v); setTime(""); }} testId="commercial-start-date" />
          <TimeField label="Start time" date={date} value={time} onChange={setTime} testId="commercial-start-time" />
        </div>

        {cat && (
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">Rate plan</label>
            <div className="space-y-2" role="radiogroup" aria-label="Rate plan" data-testid="booking-rate-plans">
              {plans.map((p) => {
                const activePlan = planId === p.rate_plan_id;
                return (
                  <button key={p.rate_plan_id} type="button" role="radio" aria-checked={activePlan}
                          onClick={() => setPlanId(p.rate_plan_id)}
                          data-testid={`booking-plan-${p.rate_plan_id}`}
                          className={`w-full text-left rounded-xl border px-4 py-3.5 backdrop-blur-sm transition-all duration-200 ${activePlan ? "border-[#E50914] bg-[#E50914]/10 shadow-lg shadow-[#E50914]/10" : "border-white/10 bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.04]"}`}>
                    <span className="flex items-center justify-between gap-3">
                      <span className={`text-sm font-bold capitalize ${activePlan ? "text-white" : "text-zinc-300"}`}>
                        {p.kind.replace("_", " ")}
                        {p.auto_renew_eligible && <span className="ml-2 text-[10px] font-bold uppercase text-[#FF3B45]">auto-renew</span>}
                      </span>
                      <span className={`font-display font-bold ${activePlan ? "text-white" : "text-zinc-200"}`}>{fmtCents(p.amount_cents, p.currency)}</span>
                    </span>
                    <span className="block mt-1 text-[11px] text-zinc-500">
                      Covers {p.duration_minutes % 43200 === 0 ? `${p.duration_minutes / 43200} month(s)` : p.duration_minutes % 1440 === 0 ? `${p.duration_minutes / 1440} day(s)` : `${Math.round(p.duration_minutes / 60)} hour(s)`} from your start time
                    </span>
                  </button>
                );
              })}
              {plans.length === 0 && <p className="text-xs text-zinc-600">No rates configured for this category yet.</p>}
            </div>
          </div>
        )}

        {selectedPlan?.kind === "monthly" && selectedPlan.auto_renew_eligible && (
          <label className="flex items-start gap-2.5 text-xs text-zinc-300 cursor-pointer" data-testid="autorenew-consent-row">
            <input type="checkbox" checked={autoRenew} onChange={(e) => setAutoRenew(e.target.checked)} className="mt-0.5" data-testid="autorenew-consent" />
            <span>
              Enable Auto-Renew — I consent to being charged {fmtCents(selectedPlan.amount_cents, selectedPlan.currency)} monthly until I cancel.
              Cancelling stops future renewals; the current paid period stays active.
            </span>
          </label>
        )}

        <div>
          <label className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-1.5">Vehicle</label>
          {vehicles === null ? <div className="h-11 rounded-xl bg-white/[0.03] animate-pulse" /> : vehicles.length === 0 ? (
            <Link to="/vehicles" className="btn-secondary w-full text-sm" data-testid="booking-add-vehicle">
              <Car className="w-4 h-4" /> Add a vehicle first
            </Link>
          ) : (
            <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className={inputCls} data-testid="booking-vehicle" aria-label="Vehicle">
              {vehicles.map((v) => <option key={v.vehicle_id} value={v.vehicle_id}>{v.nickname || `${v.make} ${v.model}`} · {v.plate}</option>)}
            </select>
          )}
        </div>

        {hostFields.length > 0 && (
          <div className="space-y-3" data-testid="booking-host-fields">
            <p className="block text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500">Property questions</p>
            {hostFields.map((f) => (
              <div key={f.field_id}>
                <label className="block text-xs text-zinc-400 mb-1.5">{f.name}{f.required && <span className="text-[#FF3B45]"> *</span>}</label>
                {f.field_type === "dropdown" ? (
                  <select className={inputCls} value={answers[f.field_id] ?? ""} data-testid={`field-${f.field_id}`} aria-label={f.name} aria-required={f.required}
                          onChange={(e) => setAnswers((a) => ({ ...a, [f.field_id]: e.target.value }))}>
                    <option value="">Select…</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.field_type === "yesno" ? (
                  <div className="flex gap-2" data-testid={`field-${f.field_id}`}>
                    {["yes", "no"].map((v) => (
                      <button type="button" key={v} onClick={() => setAnswers((a) => ({ ...a, [f.field_id]: v }))}
                              className={`flex-1 rounded-xl border px-3 py-2 text-sm transition-colors ${answers[f.field_id] === v ? "border-[#E50914] bg-[#E50914]/10 text-white" : "border-[#27272A] text-zinc-400 hover:border-zinc-600"}`}
                              data-testid={`field-${f.field_id}-${v}`}>{v === "yes" ? "Yes" : "No"}</button>
                    ))}
                  </div>
                ) : (
                  <input className={inputCls} type={f.field_type === "number" ? "number" : "text"} value={answers[f.field_id] ?? ""}
                         data-testid={`field-${f.field_id}`} aria-label={f.name} aria-required={f.required}
                         onChange={(e) => setAnswers((a) => ({ ...a, [f.field_id]: e.target.value }))} />
                )}
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-2">
          <input value={promo} onChange={(e) => setPromo(e.target.value)} placeholder="Promo code"
                 className={`${inputCls} flex-1`} data-testid="booking-promo" aria-label="Promo code" />
          <button type="button" onClick={() => { setPromoOn(true); }} disabled={!promo.trim()}
                  className="btn-secondary !py-2.5 text-xs whitespace-nowrap disabled:opacity-50" data-testid="booking-promo-apply">
            <Tag className="w-3.5 h-3.5" /> Apply
          </button>
        </div>
        {promoOn && quote?.promo && !quote.promo.applied && promo.trim() && (
          <p className="text-xs text-[#FF6B6B]" role="alert" data-testid="promo-error">{quote.promo.error || "This promo code is not valid"}</p>
        )}

        {quoteBusy && <div className="h-20 rounded-xl bg-white/[0.03] animate-pulse" />}
        {!quoteBusy && quote && q && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-1.5 text-sm" data-testid="booking-quote">
            <div className="flex justify-between"><span className="text-zinc-400">Base fare</span><span>{fmtCents(q.base_fare_cents, q.currency)}</span></div>
            {q.discount_cents > 0 && <div className="flex justify-between text-emerald-400"><span>Discount ({quote.promo.code})</span><span>-{fmtCents(q.discount_cents, q.currency)}</span></div>}
            <div className="flex justify-between"><span className="text-zinc-400">Service fee</span><span>{fmtCents(q.service_fee_cents, q.currency)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Processing fee</span><span>{fmtCents(q.processing_fee_cents, q.currency)}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Tax</span><span data-testid="checkout-tax">{q.tax_state === "calculated" ? fmtCents(q.tax_cents, q.currency) : "At confirmation"}</span></div>
            <div className="flex justify-between pt-1.5 border-t border-white/10 font-bold"><span>Total</span><span data-testid="quote-total">{fmtCents(q.total_cents, q.currency)}</span></div>
            <p className={`text-[11px] pt-1 ${quote.availability.available > 0 ? "text-zinc-500" : "text-amber-400"}`} data-testid="quote-availability">
              {quote.availability.available > 0
                ? `${quote.availability.available} of ${quote.availability.capacity} spots available`
                : `No ${quote.category_name} spots available for this time.`}
            </p>
            {quote.alternatives?.length > 0 && (
              <p className="text-[11px] text-zinc-500">Available instead: {quote.alternatives.map((a) => `${a.name} (${a.available})`).join(", ")}</p>
            )}
          </div>
        )}

        {quote && q && (
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="booking-rules">
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-zinc-500 mb-2">Rules for this booking</p>
            <ul className="space-y-1.5 text-xs text-zinc-400">
              {property.instructions && <li>· Property instructions: {property.instructions}</li>}
              {cat?.description && <li>· {cat.description}</li>}
              {hostFields.length > 0 && <li>· This property requires answers to {hostFields.length} booking question(s) at reservation</li>}
              <li>· Park only within your booked period — enforcement is the property operator's authority</li>
              <li>· Cancellation &amp; refunds follow the disclosed policy; service &amp; processing fees are non-refundable</li>
            </ul>
            <label className="mt-3 flex items-start gap-2.5 cursor-pointer">
              <input type="checkbox" checked={rulesAck} onChange={(e) => setRulesAck(e.target.checked)}
                     data-testid="booking-rules-ack" className="mt-0.5 w-4 h-4 accent-[#E50914]" />
              <span className="text-xs text-zinc-300">I have read and agree to the rules for this booking, the <Link to="/terms" target="_blank" rel="noreferrer" className="text-[#FF3B45] underline underline-offset-2">Terms</Link> and the cancellation &amp; refund policy.</span>
            </label>
          </div>
        )}

        <button onClick={() => book()} disabled={busy || !quote || !q || quote.availability.available <= 0 || !vehicleId || !rulesAck}
                className="btn-primary w-full text-sm disabled:opacity-50 disabled:cursor-not-allowed" data-testid="booking-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{autoRenew && selectedPlan?.kind === "monthly" ? "Start Auto-Renew" : "Continue to payment"} <ArrowRight className="w-4 h-4" /></>}
        </button>
        <p className="text-[11px] text-zinc-600 text-center">Your card is authorized now and charged after arrival is verified.</p>
      </div>
      {consentOpen && (
        <DriverConsent
          onAccept={() => { setConsentOpen(false); setConsent({ accepted: true }); book(true); }}
          onDecline={() => { setConsentOpen(false); toast.error("Acceptance of the Driver declaration is required to book.", { duration: 5000 }); }}
        />
      )}
    </div>
  );
}
